import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router';
import { observer } from 'mobx-react';
import { DesignTree } from 'ksnm-common-ui/lib/DesignTree';
import { IconButton } from 'ksnm-common-ui/lib/IconButton';
import { Notification } from 'ksnm-common-ui/lib/Notification';
import type { ReactElement } from 'react';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { useCheckFromDictionaryCard } from '@src/utils/hooks/useCheckFromDictionaryCard';
import { usePermission } from '@src/utils/hooks/usePermission';
import { confirmDialogStore } from '@stores/confirmDialog';
import { organizationsStore } from '@stores/dictionaryStores/organizationsStore';
import { organizationCardStore } from '@stores/dictionaryStores/organizationsStore/organizationCardStore';
import { userStore } from '@stores/userStore';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { useAuditView } from '../../hooks/useAuditView';

import styles from './Organizations.module.scss';

function OrganizationsComponent(): ReactElement | null {
  const { viewModel: organizations, fetch, organizations: organizationsData, clear } = organizationsStore;
  const { setEditMode, setNew } = organizationCardStore;

  const history = useHistory();

  useAuditView('Организация');

  useEffect(() => {
    const abortControl = new AbortController();
    (async () => {
      if (!!userStore.permissions?.CmOrganization?.scopePermissions.READ) await fetch(abortControl.signal);
    })();

    return () => {
      abortControl.abort();
    };
  }, [fetch]);

  const isBack = useCheckFromDictionaryCard(RoutePath.ORGANIZATION_CARD);

  useEffect(() => {
    if (!isBack) clear();
  }, [clear, isBack]);

  const onCreate = useCallback(
    ({ code }) => {
      setEditMode(true);
      setNew(true);
      history.push(`${RoutePath.ORGANIZATION_CARD}/${NEW_ENTITY_ID}`, {
        child: true,
        organizationCode: code ?? 0,
      });
    },
    [history, setEditMode, setNew],
  );

  const onEdit = useCallback(
    ({ code, parentCode }) => {
      setEditMode(false);
      history.push(`${RoutePath.ORGANIZATION_CARD}/${code}`, { head: !parentCode });
    },
    [history, setEditMode],
  );

  const onDelete = useCallback(
    ({ code }) =>
      !organizationsData?.some(({ parentCode }) => parentCode === code)
        ? confirmDialogStore.open({
            text: 'Удалить организацию?',
            onOk: async () => {
              await organizationCardStore.delete(code);
              organizationsStore.fetch();
            },
          })
        : Notification({
            message: 'Возможно удалить только организацию без подчиненной единицы',
            type: 'info',
          }),
    [organizationsData],
  );

  const onCreateHeadOrg = useCallback(() => {
    setNew(true);
    setEditMode(true);
    history.push(`${RoutePath.ORGANIZATION_CARD}/${NEW_ENTITY_ID}`, { head: true });
  }, [history, setEditMode, setNew]);

  const permission = usePermission('CmOrganization');

  const Title = useCallback(
    () => (
      <div className={styles.title}>
        <p>Организация</p>
        <IconButton iconName="add" onClick={onCreateHeadOrg} disabled={!permission.CREATE} title="Добавить" />
      </div>
    ),
    [onCreateHeadOrg, permission.CREATE],
  );
  return (
    <DesignLayout title={<Title />} scrollEnable>
      <div className={styles.container}>
        {organizations?.map((data, idx) => (
          <DesignTree
            key={data.key}
            selectable={false}
            treeElement={data}
            onCreate={onCreate}
            onEdit={onEdit}
            onDelete={onDelete}
            prohibitDeleteNodeWithChildren
            needCount={true}
            index={idx}
          />
        ))}
      </div>
    </DesignLayout>
  );
}

export const Organizations = observer(OrganizationsComponent);
